import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import "./estilodash.css";

const PLANOS = {
  starter: { label: "Starter", licencasPorProduto: 2 },
  trader: { label: "Trader", licencasPorProduto: 5 },
  pro: { label: "Pro", licencasPorProduto: 10 },
};

const emptyForm = {
  cliente_id: "",
  tipo: "individual",
  produto_id: "",
  pacote_id: "",
  tier: "starter",
};

function getPlanoInfo(tier) {
  return PLANOS[tier] || PLANOS.starter;
}

function getStatusValue(assinatura) {
  const raw = assinatura?.assinatura_status ?? assinatura?.status ?? 0;
  return Number(raw) === 1 ? 1 : 0;
}

function getTipoAssinatura(assinatura) {
  if (assinatura?.tipo) return assinatura.tipo;
  return assinatura?.produto_id ? "individual" : "pacote";
}

export default function AssinaturasAdmin() {
  const [assinaturas, setAssinaturas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [pacotes, setPacotes] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [busca, setBusca] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [filtroTipo, setFiltroTipo] = useState("todos");

  const [form, setForm] = useState(emptyForm);
  const [msg, setMsg] = useState("");
  const [erro, setErro] = useState("");

  useEffect(() => {
    carregarTudo();
  }, []);

  const carregarTudo = async () => {
    try {
      setLoading(true);
      setErro("");
      setMsg("");

      const [respAss, respCli, respProd, respPac] = await Promise.all([
        api.get("/api/assinaturas"),
        api.get("/api/clientes"),
        api.get("/api/produtos-ativos"),
        api.get("/api/pacotes-ativos"),
      ]);

      setAssinaturas(respAss.data || []);
      setClientes(respCli.data || []);
      setProdutos(respProd.data || []);
      setPacotes(respPac.data || []);
    } catch (err) {
      console.error("Erro ao carregar assinaturas:", err);
      setErro("Erro ao carregar assinaturas/clientes/produtos.");
    } finally {
      setLoading(false);
    }
  };

  const clientesOrdenados = useMemo(() => {
    return [...clientes].sort((a, b) =>
      String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR")
    );
  }, [clientes]);

  const assinaturasFiltradas = useMemo(() => {
    let lista = [...assinaturas];

    if (busca.trim()) {
      const termo = busca.toLowerCase();
      lista = lista.filter((a) => {
        const clienteNome = String(a.cliente_nome || "").toLowerCase();
        const clienteEmail = String(a.cliente_email || "").toLowerCase();
        const nomePlano = String(a.nome_plano || "").toLowerCase();
        const codigoPlano = String(a.codigo_plano || "").toLowerCase();

        return (
          String(a.id).includes(termo) ||
          String(a.cliente_id || "").includes(termo) ||
          clienteNome.includes(termo) ||
          clienteEmail.includes(termo) ||
          nomePlano.includes(termo) ||
          codigoPlano.includes(termo)
        );
      });
    }

    if (filtroStatus === "ativas") {
      lista = lista.filter((a) => getStatusValue(a) === 1);
    } else if (filtroStatus === "inativas") {
      lista = lista.filter((a) => getStatusValue(a) === 0);
    }

    if (filtroTipo !== "todos") {
      lista = lista.filter((a) => getTipoAssinatura(a) === filtroTipo);
    }

    return lista;
  }, [assinaturas, busca, filtroStatus, filtroTipo]);

  const assinaturasAtivasCliente = useMemo(() => {
    if (!form.cliente_id) return [];

    return assinaturas.filter(
      (a) =>
        Number(a.cliente_id) === Number(form.cliente_id) &&
        getStatusValue(a) === 1
    );
  }, [assinaturas, form.cliente_id]);

  const pacoteAtivoCliente = useMemo(() => {
    return (
      assinaturasAtivasCliente.find((a) => getTipoAssinatura(a) === "pacote") ||
      null
    );
  }, [assinaturasAtivasCliente]);

  const individuaisAtivosCliente = useMemo(() => {
    return assinaturasAtivasCliente.filter(
      (a) => getTipoAssinatura(a) === "individual"
    );
  }, [assinaturasAtivasCliente]);

  const totalIndividuaisAtivos = individuaisAtivosCliente.length;

  const produtoIdsAtivosCliente = useMemo(() => {
    return new Set(
      individuaisAtivosCliente
        .map((a) => Number(a.produto_id))
        .filter((v) => Number.isInteger(v) && v > 0)
    );
  }, [individuaisAtivosCliente]);

  const produtosDisponiveis = useMemo(() => {
    if (!form.cliente_id) return produtos;

    if (pacoteAtivoCliente) return [];

    return produtos.filter((p) => !produtoIdsAtivosCliente.has(Number(p.id)));
  }, [produtos, form.cliente_id, pacoteAtivoCliente, produtoIdsAtivosCliente]);

  const tiposDisponiveis = useMemo(() => {
    if (!form.cliente_id) {
      return [
        { value: "individual", label: "Produto individual" },
        { value: "pacote", label: "Pacote" },
      ];
    }

    if (pacoteAtivoCliente) {
      return [{ value: "pacote", label: "Pacote" }];
    }

    if (totalIndividuaisAtivos >= 2) {
      return [{ value: "pacote", label: "Pacote" }];
    }

    return [
      { value: "individual", label: "Produto individual" },
      { value: "pacote", label: "Pacote" },
    ];
  }, [form.cliente_id, pacoteAtivoCliente, totalIndividuaisAtivos]);

  useEffect(() => {
    if (!form.cliente_id) return;

    const permitido = tiposDisponiveis.some((t) => t.value === form.tipo);
    if (!permitido && tiposDisponiveis.length > 0) {
      setForm((prev) => ({
        ...prev,
        tipo: tiposDisponiveis[0].value,
        produto_id: "",
        pacote_id: "",
      }));
    }
  }, [form.cliente_id, form.tipo, tiposDisponiveis]);

  useEffect(() => {
    if (form.tipo !== "individual") return;
    if (!form.produto_id) return;

    const existe = produtosDisponiveis.some(
      (p) => String(p.id) === String(form.produto_id)
    );

    if (!existe) {
      setForm((prev) => ({
        ...prev,
        produto_id: "",
      }));
    }
  }, [form.tipo, form.produto_id, produtosDisponiveis]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => {
      const next = { ...prev, [name]: value };

      if (name === "tipo") {
        if (value === "individual") {
          next.pacote_id = "";
        } else {
          next.produto_id = "";
        }
      }

      if (name === "cliente_id") {
        next.produto_id = "";
        next.pacote_id = "";
      }

      return next;
    });
  };

  const handleCriarAssinatura = async (e) => {
    e.preventDefault();

    setErro("");
    setMsg("");

    if (!form.cliente_id) {
      setErro("Selecione o cliente.");
      return;
    }

    if (form.tipo === "individual" && !form.produto_id) {
      setErro("Selecione o produto.");
      return;
    }

    if (form.tipo === "pacote" && !form.pacote_id) {
      setErro("Selecione o pacote.");
      return;
    }

    if (form.tipo === "individual" && pacoteAtivoCliente) {
      setErro(
        "Este cliente já possui um pacote completo ativo. Não é permitido adicionar produto individual."
      );
      return;
    }

    if (form.tipo === "individual" && totalIndividuaisAtivos >= 2) {
      setErro(
        "Este cliente já possui 2 produtos individuais ativos. Para contratar os 3 robôs, utilize o pacote completo."
      );
      return;
    }

    if (
      form.tipo === "individual" &&
      produtoIdsAtivosCliente.has(Number(form.produto_id))
    ) {
      setErro("Este cliente já possui uma assinatura ativa para este produto.");
      return;
    }

    const planoInfo = getPlanoInfo(form.tier);

    try {
      setSaving(true);

      if (
        form.tipo === "pacote" &&
        individuaisAtivosCliente.length > 0
      ) {
        const confirmarMigracao = window.confirm(
          `Este cliente possui ${individuaisAtivosCliente.length} produto(s) individual(is) ativo(s).\n\nAo criar o pacote completo, as assinaturas individuais ativas serão desativadas automaticamente.\n\nDeseja continuar?`
        );

        if (!confirmarMigracao) {
          setSaving(false);
          return;
        }
      }

      const payload = {
        cliente_id: Number(form.cliente_id),
        produto_id:
          form.tipo === "individual" ? Number(form.produto_id) : null,
        pacote_id:
          form.tipo === "pacote" ? Number(form.pacote_id) : null,
        tier: form.tier,
        slots_contratados: planoInfo.licencasPorProduto,
      };

      const response = await api.post("/api/assinaturas", payload);
      const data = response.data || {};

      if (
        form.tipo === "pacote" &&
        Array.isArray(data.individuais_desativadas) &&
        data.individuais_desativadas.length > 0
      ) {
        setMsg(
          `Pacote criado com sucesso. ${data.individuais_desativadas.length} assinatura(s) individual(is) anterior(es) foram desativadas automaticamente.`
        );
      } else {
        setMsg("Assinatura criada com sucesso.");
      }

      setForm(emptyForm);
      await carregarTudo();
    } catch (err) {
      console.error("Erro ao criar assinatura:", err);
      setErro(err.response?.data?.message || "Erro ao criar assinatura.");
    } finally {
      setSaving(false);
    }
  };

  const handleAlterarPlano = async (assinatura, novoTier) => {
    if (!novoTier || novoTier === assinatura.tier) return;

    try {
      const planoInfo = getPlanoInfo(novoTier);

      await api.put(`/api/assinaturas/${assinatura.id}`, {
        tier: novoTier,
        slots_contratados: planoInfo.licencasPorProduto,
      });

      setMsg("Plano da assinatura alterado com sucesso.");
      await carregarTudo();
    } catch (err) {
      console.error("Erro ao alterar plano da assinatura:", err);
      setErro(
        err.response?.data?.message || "Erro ao alterar plano da assinatura."
      );
    }
  };

  const handleToggleStatus = async (assinatura) => {
    const statusAtual = getStatusValue(assinatura);
    const novoStatus = statusAtual === 1 ? 0 : 1;

    const confirmar = window.confirm(
      `Deseja realmente ${novoStatus === 1 ? "ATIVAR" : "DESATIVAR"} a assinatura #${assinatura.id}?`
    );
    if (!confirmar) return;

    try {
      await api.put(`/api/assinaturas/${assinatura.id}`, {
        status: novoStatus,
      });

      setMsg(
        `Assinatura ${novoStatus === 1 ? "ativada" : "desativada"} com sucesso.`
      );
      await carregarTudo();
    } catch (err) {
      console.error("Erro ao alterar status da assinatura:", err);
      setErro(
        err.response?.data?.message || "Erro ao alterar status da assinatura."
      );
    }
  };

  const handleRemoverAssinatura = async (assinatura) => {
    const confirmar = window.confirm(
      `Deseja realmente remover a assinatura #${assinatura.id} (${assinatura.nome_plano})?`
    );
    if (!confirmar) return;

    try {
      await api.delete(`/api/assinaturas/${assinatura.id}`);
      setMsg("Assinatura removida com sucesso.");
      await carregarTudo();
    } catch (err) {
      console.error("Erro ao remover assinatura:", err);
      setErro(err.response?.data?.message || "Erro ao remover assinatura.");
    }
  };

  const formatData = (dataStr) => {
    if (!dataStr) return "—";
    const d = new Date(dataStr);
    if (Number.isNaN(d.getTime())) return dataStr;

    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();

    return `${dd}/${mm}/${yyyy}`;
  };

  return (
    <div className="pagamentos-container">
      <h3>Assinaturas</h3>
      <p style={{ marginTop: -6 }}>
        Gerencie os planos contratados por cliente. Cada plano libera um limite
        de licenças <strong>por produto</strong>.
      </p>

      <div
        style={{
          background: "#f8f9fb",
          border: "1px solid #d8dbe2",
          borderRadius: 8,
          padding: 16,
          marginTop: 16,
          marginBottom: 20,
        }}
      >
        <h4 style={{ marginTop: 0 }}>Nova assinatura</h4>

        <form onSubmit={handleCriarAssinatura}>
          <div className="form-linha">
            <label>
              Cliente
              <select
                name="cliente_id"
                value={form.cliente_id}
                onChange={handleFormChange}
              >
                <option value="">Selecione...</option>
                {clientesOrdenados.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.id} - {c.nome} ({c.email})
                  </option>
                ))}
              </select>
            </label>

            <label>
              Tipo
              <select
                name="tipo"
                value={form.tipo}
                onChange={handleFormChange}
              >
                {tiposDisponiveis.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {form.cliente_id && pacoteAtivoCliente && (
            <div
              style={{
                background: "#fff8e1",
                color: "#8a6d3b",
                border: "1px solid #f2d58a",
                borderRadius: 6,
                padding: 10,
                marginBottom: 12,
              }}
            >
              Este cliente já possui <strong>pacote ativo</strong>. Não é permitido adicionar produto individual.
            </div>
          )}

          {form.cliente_id && !pacoteAtivoCliente && totalIndividuaisAtivos > 0 && (
            <div
              style={{
                background: "#eef6ff",
                color: "#1d4e89",
                border: "1px solid #bfd8ff",
                borderRadius: 6,
                padding: 10,
                marginBottom: 12,
              }}
            >
              Este cliente possui <strong>{totalIndividuaisAtivos}</strong> produto(s) individual(is) ativo(s).
              {totalIndividuaisAtivos < 2
                ? " Ainda é possível adicionar mais 1 produto individual ou migrar para o pacote completo."
                : " O limite de individuais foi atingido. Para ter os 3 robôs, utilize o pacote completo."}
            </div>
          )}

          <div className="form-linha">
            {form.tipo === "individual" ? (
              <label>
                Produto
                <select
                  name="produto_id"
                  value={form.produto_id}
                  onChange={handleFormChange}
                  disabled={!!pacoteAtivoCliente || totalIndividuaisAtivos >= 2}
                >
                  <option value="">Selecione.</option>
                  {produtosDisponiveis.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nome} ({p.codigo})
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <label>
                Pacote
                <select
                  name="pacote_id"
                  value={form.pacote_id}
                  onChange={handleFormChange}
                >
                  <option value="">Selecione.</option>
                  {pacotes.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nome} ({p.codigo})
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label>
              Tier
              <select name="tier" value={form.tier} onChange={handleFormChange}>
                {Object.entries(PLANOS).map(([key, plano]) => (
                  <option key={key} value={key}>
                    {plano.label} ({plano.licencasPorProduto} licenças por produto)
                  </option>
                ))}
              </select>
            </label>
          </div>

          {form.tipo === "individual" && form.cliente_id && !pacoteAtivoCliente && (
            <div
              style={{
                background: "#fafafa",
                border: "1px solid #ddd",
                borderRadius: 6,
                padding: 10,
                marginBottom: 12,
                color: "#333",
              }}
            >
              <strong>Produtos já ativos:</strong>{" "}
              {individuaisAtivosCliente.length === 0
                ? "nenhum"
                : individuaisAtivosCliente
                    .map((a) => a.nome_plano || a.codigo_plano || `Produto #${a.produto_id}`)
                    .join(", ")}
            </div>
          )}

          {form.tipo === "pacote" && form.cliente_id && individuaisAtivosCliente.length > 0 && (
            <div
              style={{
                background: "#fff8e1",
                color: "#8a6d3b",
                border: "1px solid #f2d58a",
                borderRadius: 6,
                padding: 10,
                marginBottom: 12,
              }}
            >
              Ao criar o <strong>pacote completo</strong>, as assinaturas individuais ativas deste cliente serão
              <strong> desativadas automaticamente</strong>.
            </div>
          )}

          <div style={{ marginTop: 10 }}>
            <button type="submit" disabled={saving}>
              {saving ? "Salvando..." : "Criar assinatura"}
            </button>
          </div>
        </form>
      </div>

      {msg && (
        <div
          style={{
            background: "#e8f6ed",
            color: "#14532d",
            border: "1px solid #b7e4c7",
            borderRadius: 6,
            padding: 10,
            marginBottom: 12,
          }}
        >
          {msg}
        </div>
      )}

      {erro && (
        <div
          style={{
            background: "#fdecec",
            color: "#7f1d1d",
            border: "1px solid #f5b7b7",
            borderRadius: 6,
            padding: 10,
            marginBottom: 12,
          }}
        >
          {erro}
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr 1fr",
          gap: 12,
          marginBottom: 14,
        }}
      >
        <input
          type="text"
          placeholder="Buscar por cliente, email, produto, pacote..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />

        <select
          value={filtroTipo}
          onChange={(e) => setFiltroTipo(e.target.value)}
        >
          <option value="todos">Todos os tipos</option>
          <option value="individual">Produto individual</option>
          <option value="pacote">Pacote</option>
        </select>

        <select
          value={filtroStatus}
          onChange={(e) => setFiltroStatus(e.target.value)}
        >
          <option value="todos">Todos os status</option>
          <option value="ativas">Ativas</option>
          <option value="inativas">Inativas</option>
        </select>
      </div>

      {loading ? (
        <p>Carregando assinaturas...</p>
      ) : assinaturasFiltradas.length === 0 ? (
        <p>Nenhuma assinatura encontrada.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="clientes-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Cliente</th>
                <th>Tipo</th>
                <th>Produto/Pacote</th>
                <th>Plano</th>
                <th>Licenças por produto</th>
                <th>Licenças ativas</th>
                <th>Status</th>
                <th>Criada em</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {assinaturasFiltradas.map((a) => {
                const status = getStatusValue(a);
                const tipo = getTipoAssinatura(a);
                const plano = getPlanoInfo(a.tier);

                return (
                  <tr key={a.id}>
                    <td>{a.id}</td>
                    <td>
                      <div style={{ textAlign: "left" }}>
                        <strong>{a.cliente_nome || `Cliente #${a.cliente_id}`}</strong>
                        <br />
                        <small>{a.cliente_email || "—"}</small>
                      </div>
                    </td>
                    <td>{tipo === "pacote" ? "Pacote" : "Individual"}</td>
                    <td>{a.nome_plano || a.codigo_plano || "—"}</td>
                    <td>
                      <select
                        value={a.tier || "starter"}
                        onChange={(e) => handleAlterarPlano(a, e.target.value)}
                      >
                        {Object.entries(PLANOS).map(([key, item]) => (
                          <option key={key} value={key}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                      <div style={{ fontSize: 12, color: "#555", marginTop: 4 }}>
                        {plano.licencasPorProduto} por produto
                      </div>
                    </td>
                    <td>{a.slots_contratados || plano.licencasPorProduto}</td>
                    <td>{a.licencas_ativas || 0}</td>
                    <td>
                      <span className={status === 1 ? "badge badge-ativo" : "badge badge-inativo"}>
                        {status === 1 ? "Ativa" : "Inativa"}
                      </span>
                    </td>
                    <td>{formatData(a.created_on)}</td>
                    <td>
                      <button type="button" onClick={() => handleToggleStatus(a)}>
                        {status === 1 ? "Desativar" : "Ativar"}
                      </button>
                      <button type="button" className="btn-remover" onClick={() => handleRemoverAssinatura(a)}>
                        Remover
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}